import React from "react";
import { Helmet } from "react-helmet";
import { graphql, useStaticQuery } from "gatsby";

import Content from "../components/Content";
import Card, { CardContainer } from "../components/Card";
import HeroImage from "../components/HeroImage";
import { useWelshMap } from "../useWelsh";
import newsletter from "../documents/Community newsletter - June 2021.pdf";

const map = {
  english: {
    info1: `Welcome to our consultation website!
    Great House Energy Centre Limited, a subsidiary of Renewable Connections, has been assessing an opportunity to develop a solar farm on land near Abergavenny in Monmouthshire since early 2020.  If constructed, the project will generate enough electricity to power over 11,000 homes, and make a significant contribution to Welsh climate commitments.
    Since early 2020, we have been undertaking extensive surveys and studies across the site.  We also undertook preliminary engagement with the community and stakeholders earlier this year.  Now we are ready to share our proposals and the assessments that have been undertaken in support of these designs through a period of formal consultation.
    Consultation will run for a period of 8 weeks between 30th June 2021 and 25th August 2021.  As well as our |newsletter|  which has been sent to local residences, we will also be hosting two virtual community consultation events where we will brief local people on our proposals and answer any questions that people have.`,
    event: {
      title: "Community Consultation event registration",
      text1: "Our public information events will be held as follows:",
      link1: "Wednesday 28th July at 7pm",
      link2: "Thursday 5th August at 1pm",
      text2: "To register for either event, please follow the links above.",
    },
    info2:
      "Please use the links below to learn more about our proposed project, the proposed timeline and how it will help to meet Welsh renewable energy targets. We would welcome any feedback you may have.",
    card1: {
      title: "The Project",
      text: "Read our proposal for the new development",
    },
    card2: {
      title: "The Process",
      text: "Read about how the planning process works",
    },
    card3: {
      title: "Project Documents",
      text: "Read our draft planning application",
    },
    readMore: "Read more",
  },
  welsh: {
    info: `Mae Renewable Connections wedi bod yn ystyried cyfle i ddatblygu fferm solar ar dir ger y Fenni yn Sir Fynwy ers dechrau 2020. Lleolir y safle hwn i'r gogledd o Benpergwm ar gaeau amaethyddol a ddefnyddir ar hyn o bryd ar gyfer ffermio cnydau ac fel porfa i anifeiliaid.
    Os caiff ei adeiladu, bydd y prosiect yn cynhyrchu digon o drydan i bweru dros 13,000 o gartrefi drwy baneli solar effaith isel a osodir uwchben y ddaear. Mae hyn yn golygu bod modd parhau i ffermio da byw a bydd bywyd gwyllt yn gallu ffynnu. Bydd y prosiect yn gyfrifol am leihau allyriadau CO2 i tua 11,000 tunnell y flwyddyn a bydd yn chwarae rhan bwysig wrth helpu i ymateb i'r Argyfwng Hinsawdd.
    Defnyddiwch y dolenni isod i ddysgu mwy am ein prosiect arfaethedig, yr amserlen arfaethedig a sut y bydd yn helpu i gyrraedd targedau ynni adnewyddadwy Cymru. Mae croeso i unrhyw adborth a allai fod gennych ar ein cynigion.`,
    card1: {
      title: "Y Cynnig",
      text: "",
    },
    card2: {
      title: "Y Broses",
      text: "",
    },
    card3: {
      title: "Cwestiynau Cyffredin",
      text: "",
    },
    status: {
      title: "Y Newyddion Diweddaraf",
      text: `Renewable Connections is reflecting on feedback received from stakeholders and our public engagement.  We have published additional FAQs to the website and will be initiating an 8 week consultation process later in the Spring. Please continue to visit our website for further updates.`,
    },
    readMore: "Mwy o Wybodaeth",
  },
};

const Main = () => {
  const text = useWelshMap(map);
  const { proposal, process, faq } = useStaticQuery(graphql`
    query {
      proposal: file(relativePath: { eq: "Stock3.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 350) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      process: file(relativePath: { eq: "Stock2.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 350, quality: 80) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      faq: file(relativePath: { eq: "sheep.jpg" }) {
        childImageSharp {
          fluid(maxWidth: 350, quality: 100) {
            ...GatsbyImageSharpFluid
          }
        }
      }
    }
  `);

  return (
    <>
      <Helmet>
        <title>Penpergwm Solar Farm</title>
      </Helmet>
      <HeroImage />
      <Content>
        {text.info1.split("\n").map((words, i) => (
          <p key={i}>
            {words.split("*").map((x, j) =>
              j % 2 ? (
                <b key={j}>{x}</b>
              ) : (
                x.split("|").map((y, l) =>
                  l % 2 ? (
                    <a href={newsletter} key={l}>
                      {y}
                    </a>
                  ) : (
                    y
                  )
                )
              )
            )}
          </p>
        ))}
      </Content>
      <Content color="grey">
        <h2>{text.event.title}</h2>
        <p>{text.event.text1}</p>
        <ul>
          <li>
            <a href="https://us02web.zoom.us/webinar/register/WN_1ur9EsylRUm6-GsaT0EGvw">
              {text.event.link1}
            </a>
          </li>
          <li>
            <a href="https://us02web.zoom.us/webinar/register/WN_l5yMexLOQeqeA1B3V7IxAw">
              {text.event.link2}
            </a>
          </li>
        </ul>
        <p>{text.event.text2}</p>
      </Content>
      <Content>
        <p>{text.info2}</p>
      </Content>
      <Content>
        <CardContainer>
          <Card
            title={text.card1.title}
            text={text.card1.text}
            link="/proposal"
            linkText={text.readMore}
            image={proposal.childImageSharp.fluid}
          />
          <Card
            title={text.card2.title}
            text={text.card2.text}
            link="/process"
            linkText={text.readMore}
            image={process.childImageSharp.fluid}
          />
          <Card
            title={text.card3.title}
            text={text.card3.text}
            link="/frequently-asked-questions"
            linkText={text.readMore}
            image={faq.childImageSharp.fluid}
          />
        </CardContainer>
      </Content>
    </>
  );
};

export default Main;
